import React, { useEffect } from 'react';
import { Box, Flex, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';
import OptionComponent from './OptionComponent';
import ButtonComponent from './ButtonComponent';
import SpacerComponent from "./SpacerComponent";
import ListComponent from './ListComponent';
import ContentDisplayComponent from './ContentDisplayComponent';
import { areRequiredFieldsValid } from '../Utils/utils';
import DOMPurify from 'dompurify';

const MultiQuestionnaireComponent = ({
  questions,
  buttons = [],
  formData,
  readOnly,
  priorDecisions = {},
  updateFormData,
  handleButtonAction,
  onOptionSelect,
  isTouchDevice,
  isMouseDevice
}) => {
  // Initialize formData with isSelected values on mount for all questions
  useEffect(() => {
    questions.forEach(question => {
      if (!formData[question.id]?.value) {
        const initiallySelectedValues = question.options
          .filter((option) => option.isSelected === 1 && option.isVisible !== 'false')
          .map((option) => option.value);
        if (initiallySelectedValues.length > 0) {
          updateFormData(
            question.id,
            initiallySelectedValues,
            question.options
              .filter((opt) => initiallySelectedValues.includes(opt.value))
              .map((opt) => opt.label)
              .join(', '),
            question.metric,
            question.flowSheetId
          );
        }
      }
    });
  }, [questions, formData, updateFormData]);

  const handleChange = (questionId, value, isCheckbox = false, selectedOptionOverride = null) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    if (isCheckbox) {
      const currentValues = Array.isArray(formData[questionId]?.value)
        ? formData[questionId].value
        : [];
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      updateFormData(
        questionId,
        updatedValues,
        updatedValues
          .map(
            (val) => question.options.find((opt) => opt.value === val)?.label
          )
          .join(', '),
        question.metric,
        question.flowSheetId
      );
    } else {
      const selectedOption = selectedOptionOverride || question.options.find(
        (opt) => opt.value === value
      );
      updateFormData(
        questionId,
        value,
        selectedOption?.label || '',
        question.metric,
        question.flowSheetId
      );

      if (onOptionSelect) {
        onOptionSelect(selectedOption);
      }
    }
  };

  const handleSelectAll = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const visibleOptions = question.options.filter(option => option.isVisible !== "false");
    const allOptionValues = visibleOptions.map(option => option.value);
    const selectedValue = formData[questionId]?.value || [];
    const currentValues = Array.isArray(selectedValue) ? selectedValue : [];
    const areAllVisibleOptionsSelected = allOptionValues.every(value => currentValues.includes(value));

    if (areAllVisibleOptionsSelected) {
      // If all are selected, deselect all
      updateFormData(
        questionId,
        [],
        '',
        question.metric,
        question.flowSheetId
      );

    } else {
      // If not all are selected, select all
      updateFormData(
        questionId,
        allOptionValues,
        visibleOptions.map(option => option.label).join(', '),
        question.metric,
        question.flowSheetId
      );
    }
  };

  const isOptionDisabled = (value) =>
    Object.values(priorDecisions).includes(value);

  // Check if all required fields across all questions are valid
  const allRequiredFields = questions.reduce((acc, question) => {
    return acc.concat(question.required || []);
  }, []);

  const isButtonDisabled = !areRequiredFieldsValid(formData, allRequiredFields);

  const renderChoiceGroup = (question) => {
    const selectedValue = formData[question.id]?.value || [];
    const selectedLabel = formData[question.id]?.label || null;
    const valueCounts = (question.options || []).reduce((acc, option) => {
      const key = String(option.value);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const isSingleOptionSelected = (option) => {
      if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
        return false;
      }

      if (selectedValue != option.value) {
        return false;
      }

      const hasDuplicateValue = (valueCounts[String(option.value)] || 0) > 1;
      if (hasDuplicateValue && selectedLabel) {
        return selectedLabel === option.label;
      }

      return true;
    };

    if (question.view === 'list') {
      return (
        <Stack>
          <Stack direction="column" spacing={4}>
              {question.options
                .filter(option => option.isVisible !== "false")
                .map((option, index) => (
                  <OptionComponent
                    key={index}
                    option={option}
                    isSelected={isSingleOptionSelected(option)}
                    onSelect={() => handleChange(question.id, option.value, false, option)}
                    view={question.view}
                    choice={question.choice}
                    isDisabled={isOptionDisabled(option.value)}
                    variant={option.variant || 'body-sm-min-reg'}
                    ariaLabel={option.ariaLabel || `Option: ${option.label}`}
                  />
                ))}
          </Stack>
        </Stack>
      );
    }

    if (['grid', 'box'].includes(question.view)) {
      return (
        <SimpleGrid
          columns={question.columns || 2}
          spacing={question.spacing || 2}
        >
            {question.options
                .filter(option => option.isVisible !== "false")
                .map((option, index) => (
                  <OptionComponent
                    key={index}
                    option={option}
                    isSelected={
                      question.choice === 'multiple'
                        ? selectedValue.includes(option.value)
                        : isSingleOptionSelected(option)
                    }
                    onSelect={() =>
                      handleChange(question.id, option.value, question.choice === 'multiple', option)
                    }
                    view={question.view}
                    choice={question.choice}
                    isDisabled={isOptionDisabled(option.value)}
                    variant={option.variant || 'body-sm-min-reg'}
                    iconVariant={option.iconVariant}
                    ariaLabel={option.ariaLabel || `Option: ${option.label}`}
                  />
                ))}
        </SimpleGrid>
      );
    }

    if (question.view === 'card') {
      return (
        <Stack spacing={question.spacing || 4} mb={question.marginBottom ? question.marginBottom : 4}>
            {question.options
                .filter(option => option.isVisible !== "false")
                .map((option, index) => (
                  <OptionComponent
                    key={index}
                    option={option}
                    isSelected={
                      question.choice === 'multiple'
                        ? selectedValue.includes(option.value)
                        : isSingleOptionSelected(option)
                    }
                    onSelect={() =>
                      handleChange(question.id, option.value, question.choice === 'multiple', option)
                    }
                    view={question.view}
                    choice={question.choice}
                    isDisabled={isOptionDisabled(option.value)}
                    variant={option.variant || 'body-sm-min-reg'}
                    iconVariant={option.iconVariant}
                    ariaLabel={option.ariaLabel || `Option: ${option.label}`}
                  />
                ))}
        </Stack>
      );
    }

    if (question.view === 'flow') {
      return (
        <Box display="flex" flexWrap="wrap" gap={4}>
            {question.options
                .filter(option => option.isVisible !== "false")
                .map((option, index) => (
                  <OptionComponent
                    key={index}
                    option={option}
                    isSelected={
                      question.choice === 'multiple'
                        ? selectedValue.includes(option.value)
                        : isSingleOptionSelected(option)
                    }
                    onSelect={() =>
                      handleChange(question.id, option.value, question.choice === 'multiple', option)
                    }
                    view={question.view}
                    choice={question.choice}
                    isDisabled={isOptionDisabled(option.value)}
                    variant={option.variant || 'body-sm-min-reg'}
                    iconVariant={option.iconVariant}
                    ariaLabel={option.ariaLabel || `Option: ${option.label}`}
                  />
                ))}
        </Box>
      );
    }

    return null;
  };

  const renderContentBlock = (block, blockIndex, question) => {
    const selectedValue = formData[question.id]?.value || [];
    const visibleOptions = question.options.filter(option => option.isVisible !== "false");
    const allOptionValues = visibleOptions.map(option => option.value);
    const currentValues = Array.isArray(selectedValue) ? selectedValue : [];
    const areAllVisibleOptionsSelected = allOptionValues.every(value => currentValues.includes(value));

    switch (block.type) {
      case 'list':
        return (
          <Box key={`block-${blockIndex}`} mb={6}>
            <ListComponent
              items={block.items}
              styleType={block.styleType || 'custom-circle'}
              variant={block.variant || 'body-md-min-reg'}
              icon={block.icon}
              markerColor={block.markerColor}
              itemColor={block.itemColor}
              marginTop={block.marginTop}
              marginBottom={block.marginBottom}
            />
          </Box>
        );
      
      case 'content-list':
        return (
          <Box key={`block-${blockIndex}`} mb={6}>
            <ContentDisplayComponent 
              type="content-list" 
              items={block.items} 
              marginBottom={block.marginBottom}
              marginTop={block.marginTop}
            />
          </Box>
        );
      
      case 'helper-text':
        return (
          <Text
            key={`block-${blockIndex}`}
            style={{
              fontSize: `var(--${block.variant || 'body-sm-min-reg'}-font-size)`,
              fontWeight: `var(--${block.variant || 'body-sm-min-reg'}-font-weight)`,
              color: `var(--${block.variant || 'body-sm-min-reg'}-color, var(--sutter-sync-purple))`,
            }}
            mt={-2}
            mb={2}
          >
            {block.text}
          </Text>
        );
      
      case 'select-all-button':
        return (
          <Box key={`block-${blockIndex}`} mb={4} display="flex" justifyContent="flex-end">
            {question.button
              .filter(btn => btn.action === 'select-all')
              .map((btn, btnIndex) => {
                const buttonLabel = areAllVisibleOptionsSelected ? (btn.deselectLabel || 'Deselect all') : (btn.label || 'Select all');
                const buttonVariant = buttonLabel.toLowerCase().includes('deselect all')
                  ? 'deselect-all'
                  : buttonLabel.toLowerCase().includes('select all')
                  ? 'select-all'
                  : (btn.variant || 'outline-half');
                const buttonAriaLabel = areAllVisibleOptionsSelected ? (btn.deselectAriaLabel || 'Deselect all options') : (btn.ariaLabel || 'Select all options');
                const buttonTooltip = areAllVisibleOptionsSelected ? (btn.deselectTooltip || 'Click to deselect all conditions') : (btn.tooltip || 'Click to select all conditions');

                return (
                  <ButtonComponent
                    key={`select-all-${question.id}-${btnIndex}`}
                    label={buttonLabel}
                    variant={buttonVariant}
                    isDisabled={false}
                    ariaLabel={buttonAriaLabel}
                    tooltip={buttonTooltip}
                    onClick={() => handleSelectAll(question.id)}
                    marginBottom={'0'}
                    isTouchDevice={isTouchDevice}
                    isMouseDevice={isMouseDevice}
                  />
                );
              })}
          </Box>
        );
      
      case 'options':
        return (
          <Box key={`block-${blockIndex}`}>
            {renderChoiceGroup(question)}
          </Box>
        );
      
      default:
        console.warn(`Unknown content block type: ${block.type}`);
        return null;
    }
  };

  const renderQuestion = (question, index) => {
    const selectedValue = formData[question.id]?.value || [];
    const visibleOptions = question.options.filter(option => option.isVisible !== "false");
    const allOptionValues = visibleOptions.map(option => option.value);
    const currentValues = Array.isArray(selectedValue) ? selectedValue : [];
    const areAllVisibleOptionsSelected = allOptionValues.every(value => currentValues.includes(value));

    // If contentBlocks is specified, use flexible ordering
    if (question.contentBlocks && Array.isArray(question.contentBlocks)) {
      return (
        <Box key={question.id} mb={8}>
          {/* Question Title */}
          {question.title && (
            <Text
              style={{
                fontSize: `var(--${question.titleVariant || 'headline-md-max'}-font-size)`,
                fontWeight: `var(--${question.titleVariant || 'headline-md-max'}-font-weight)`,
                color: `var(--${question.titleVariant || 'headline-md-max'}-color, var(--sutter-sync-purple))`,
              }}
              mt={2}
              mb={4}
            >
              {question.title}
            </Text>
          )}

          {/* Question Prompt */}
          <Text
            style={{
              fontSize: `var(--${question.variant}-font-size)`,
              fontWeight: `var(--${question.variant}-font-weight)`,
              color: `var(--${question.variant}-color, var(--sutter-sync-purple))`,
            }}
            mt={question.title ? 0 : 2}
            mb={4}
          >
            {question.prompt}
          </Text>

          {/* Render content blocks in specified order */}
          {question.contentBlocks.map((block, blockIndex) => renderContentBlock(block, blockIndex, question))}
        </Box>
      );
    }

    // Fallback to original fixed structure
    return (
      <Box key={question.id} mb={8}>
        {/* Question Title */}
        {question.title && (
          <Text
            style={{
              fontSize: `var(--${question.titleVariant || 'headline-md-max'}-font-size)`,
              fontWeight: `var(--${question.titleVariant || 'headline-md-max'}-font-weight)`,
              color: `var(--${question.titleVariant || 'headline-md-max'}-color, var(--sutter-sync-purple))`,
            }}
            mt={2}
            mb={4}
          >
            {question.title}
          </Text>
        )}

        {/* Question Prompt */}
        <Text
          style={{
            fontSize: `var(--${question.variant}-font-size)`,
            fontWeight: `var(--${question.variant}-font-weight)`,
            color: `var(--${question.variant}-color, var(--sutter-sync-purple))`,
          }}
          mt={question.title ? 0 : 2}
          mb={4}
        >
          {question.prompt}
        </Text>

        {/* List Content (between prompt and options) */}
        {question.list && (
          <Box mb={6}>
            <ListComponent
              items={question.list.items}
              styleType={question.list.styleType || 'custom-circle'}
              variant={question.list.variant || 'body-md-min-reg'}
              icon={question.list.icon}
              markerColor={question.list.markerColor}
              itemColor={question.list.itemColor}
              marginTop={question.list.marginTop}
              marginBottom={question.list.marginBottom}
            />
          </Box>
        )}

        {/* Content List (between prompt and options) */}
        {question.contentList && (
          <Box mb={6}>
            <ContentDisplayComponent 
              type="content-list" 
              items={question.contentList.items} 
              marginBottom={question.contentList.marginBottom}
              marginTop={question.contentList.marginTop}
            />
          </Box>
        )}
        
        {/* Helper Text */}
        {question.helperText && (
          <Text
            style={{
              fontSize: `var(--${question.helperTextVariant}-font-size)`,
              fontWeight: `var(--${question.helperTextVariant}-font-weight)`,
              color: `var(--${question.helperTextVariant}-color, var(--sutter-sync-purple))`,
            }}
            mt={-2}
            mb={2}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.helperText) }}
          />
        )}

        {/* Select All/Deselect All Button - positioned between prompt and options */}
        {!readOnly && question.button?.some(btn => btn.action === 'select-all') && (
          <Box mb={4} display="flex" justifyContent="flex-end">
            {question.button
              .filter(btn => btn.action === 'select-all')
              .map((btn, btnIndex) => {
                const buttonLabel = areAllVisibleOptionsSelected ? (btn.deselectLabel || 'Deselect all') : (btn.label || 'Select all');
                const buttonVariant = buttonLabel.toLowerCase().includes('deselect all')
                  ? 'deselect-all'
                  : buttonLabel.toLowerCase().includes('select all')
                  ? 'select-all'
                  : (btn.variant || 'outline-half');
                const buttonAriaLabel = areAllVisibleOptionsSelected ? (btn.deselectAriaLabel || 'Deselect all options') : (btn.ariaLabel || 'Select all options');
                const buttonTooltip = areAllVisibleOptionsSelected ? (btn.deselectTooltip || 'Click to deselect all conditions') : (btn.tooltip || 'Click to select all conditions');

                return (
                  <ButtonComponent
                    key={`select-all-${question.id}-${btnIndex}`}
                    label={buttonLabel}
                    variant={buttonVariant}
                    isDisabled={false} // This button should always be enabled
                    ariaLabel={buttonAriaLabel}
                    tooltip={buttonTooltip}
                    onClick={() => handleSelectAll(question.id)}
                    marginBottom={'0'}
                    isTouchDevice={isTouchDevice}
                    isMouseDevice={isMouseDevice}
                  />
                );
              })}
          </Box>
        )}

        {/* Render Choice Group */}
        {renderChoiceGroup(question)}
      </Box>
    );
  };

  // Use buttons passed from the section level

  return (
    <Flex
      direction="column"
      height="100%"
      justify="flex-start"
    >
      {/* Render all questions */}
      <VStack spacing={6} align="stretch">
        {questions.map((question, index) => renderQuestion(question, index))}
      </VStack>

      <SpacerComponent />
      
      {/* Button - shared across all questions */}
      {!readOnly && buttons.length > 0 && (
        <Box>
          {buttons
            .filter(btn => btn.action !== 'select-all') // Exclude select-all from bottom buttons
            .map((btn, index) => {
            const isBackButton = btn.action === 'back';
            const isNoThanksButton = btn.label?.toLowerCase().includes('no thanks');
            const hasAnySelection = questions.some((question) => {
              const value = formData?.[question.id]?.value;
              if (Array.isArray(value)) {
                return value.some(v => v !== '' && v !== null && v !== undefined);
              }
              return value !== undefined && value !== null && value !== '';
            });
            const disableNoThanksWhenSelected = isNoThanksButton && hasAnySelection;
            const isDisabled = isBackButton
              ? false
              : (disableNoThanksWhenSelected || (isButtonDisabled && !isNoThanksButton));

            // Determine the variant logic
            let variant = btn.variant || 'solid';

            // If button is disabled, check for 'half' in variant and use 'disabled-half'
            if (!isBackButton && isDisabled) {
              variant = btn.variant?.includes('half') ? 'disabled-half' : 'disabled';
            }

            return (
              <ButtonComponent
                key={index}
                label={btn.label || 'Next'}
                variant={variant}
                isDisabled={isDisabled}
                ariaLabel={btn.ariaLabel || ''}
                tooltip={btn.tooltip || ''}
                onClick={() => handleButtonAction(btn.action, btn)}
                marginBottom={'5px'}
                isTouchDevice={isTouchDevice}
                isMouseDevice={isMouseDevice}
              />
            );
          })}
        </Box>
      )}
    </Flex>
  );
};

export default MultiQuestionnaireComponent;
